import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider } from './context/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';

/** Scrolls to the top of the page on every route change. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/** Redirects to `to` while preserving the incoming query string (e.g. ?ref=ABC). */
function RedirectPreservingQuery({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

import ProtectedRoute   from './components/auth/ProtectedRoute';

import WaitlistPage      from './pages/WaitlistPage';
import SignupPage        from './pages/SignupPage';
import LoginPage         from './pages/LoginPage';
import AuthCallbackPage  from './pages/AuthCallbackPage';
import OAuthConsentPage  from './pages/OAuthConsentPage';
import PricingPage       from './pages/PricingPage';
import OnboardingPage    from './pages/OnboardingPage';
import DashboardPage     from './pages/DashboardPage';
import RecipientPortalPage   from './pages/RecipientPortalPage';
import WrappedPage           from './pages/WrappedPage';
import WrappedSharePage      from './pages/WrappedSharePage';
import PrivacyPage           from './pages/PrivacyPage';
import TermsPage             from './pages/TermsPage';
import AdminPage             from './pages/AdminPage';
import DesignLibraryPage     from './pages/DesignLibraryPage';
import CalendarPage          from './pages/CalendarPage';
import AccountPage           from './pages/AccountPage';
import ContactPage           from './pages/ContactPage';
import AboutPage             from './pages/AboutPage';
import ComparePage           from './pages/ComparePage';
import ComparisonHubPage     from './pages/ComparisonHubPage';
import OccasionHubPage       from './pages/OccasionHubPage';
import OccasionPage          from './pages/OccasionPage';
import WhatToWriteBirthdayPage from './pages/WhatToWriteBirthdayPage';
import WhatToWriteChristmasPage from './pages/WhatToWriteChristmasPage';
import WhatToWriteAnniversaryPage from './pages/WhatToWriteAnniversaryPage';
import WhatToWriteSympathyPage from './pages/WhatToWriteSympathyPage';
import NotFoundPage          from './pages/NotFoundPage';
import ResetPasswordPage     from './pages/ResetPasswordPage';

/**
 * Root layout — wraps every route with the app-wide providers.
 *
 * This used to live in main.tsx as JSX around <BrowserRouter>. It moved into a
 * pathless layout route so the app can be driven by a data router, which is
 * what vite-react-ssg needs to pre-render the public marketing pages to static
 * HTML at build time (for SEO + AI search crawlers that don't run JavaScript).
 */
function RootLayout() {
  // NOTE: No <HelmetProvider> here on purpose. vite-react-ssg wraps the app in
  // its own HelmetProvider on both the server (to extract <head> into the
  // static HTML) and the client. Nesting a second provider would intercept our
  // <Helmet> output into a separate context, leaving the prerendered <head>
  // empty — which is why per-page title/meta/canonical/JSON-LD used to be
  // missing from the static HTML. react-helmet-async is pinned to ^1.3.0 to
  // match the version vite-react-ssg's head extractor is built against.
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ScrollToTop />
        <Outlet />
      </AuthProvider>
    </ErrorBoundary>
  );
}

/**
 * Route map (RouteObject[] for the data router):
 *
 *  PUBLIC (pre-rendered to static HTML — see ssgOptions in vite.config.ts)
 *    /              Marketing landing page (hero animation, features, CTA → /signup)
 *    /pricing       Subscription tier comparison + trial CTA
 *    /about         Founder story
 *    /contact       Support / contact
 *    /privacy       Legal
 *    /terms         Legal
 *    /designs       Card design library (public browse)
 *
 *  PUBLIC (client-rendered via SPA fallback)
 *    /join          Redirects to /signup, preserving ?ref= referral param
 *    /signup        Email/password signup + consent
 *    /login         Email/password + Google/Apple SSO
 *    /auth/callback OAuth PKCE exchange
 *    /auth/reset-password
 *    /preview/:token        Recipient card preview (dynamic, no auth)
 *    /wrapped/s/:token      Relationship Wrapped share (dynamic, no auth)
 *
 *  SEMI-PROTECTED (requires session, not full profile)
 *    /auth/consent  Consent collection for OAuth users
 *
 *  PROTECTED (requires session + completed consent)
 *    /dashboard /onboarding /wrapped /calendar /account /admin
 */
export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      // ── Public (pre-rendered) ──────────────────────────────────────────
      { path: '/',        element: <WaitlistPage /> },
      { path: '/pricing', element: <PricingPage /> },
      { path: '/about',   element: <AboutPage /> },
      { path: '/contact', element: <ContactPage /> },
      { path: '/privacy', element: <PrivacyPage /> },
      { path: '/terms',   element: <TermsPage /> },
      { path: '/designs', element: <DesignLibraryPage /> },
      { path: '/compare',  element: <ComparisonHubPage /> },
      { path: '/vs/:slug', element: <ComparePage /> },
      { path: '/occasions',            element: <OccasionHubPage /> },
      { path: '/birthday-cards',       element: <OccasionPage slug="birthday" /> },
      { path: '/anniversary-cards',    element: <OccasionPage slug="anniversary" /> },
      { path: '/christmas-cards',      element: <OccasionPage slug="christmas" /> },
      { path: '/valentines-day-cards', element: <OccasionPage slug="valentines-day" /> },
      { path: '/mothers-day-cards',    element: <OccasionPage slug="mothers-day" /> },
      { path: '/fathers-day-cards',    element: <OccasionPage slug="fathers-day" /> },
      { path: '/graduation-cards',     element: <OccasionPage slug="graduation" /> },
      { path: '/thinking-of-you-cards', element: <OccasionPage slug="thinking-of-you" /> },
      { path: '/what-to-write-in-a-birthday-card',    element: <WhatToWriteBirthdayPage /> },
      { path: '/what-to-write-in-a-christmas-card',   element: <WhatToWriteChristmasPage /> },
      { path: '/what-to-write-in-an-anniversary-card', element: <WhatToWriteAnniversaryPage /> },
      { path: '/what-to-write-in-a-sympathy-card',    element: <WhatToWriteSympathyPage /> },

      // ── Public (client-rendered) ───────────────────────────────────────
      { path: '/join',                element: <RedirectPreservingQuery to="/signup" /> },
      { path: '/signup',              element: <SignupPage /> },
      { path: '/login',               element: <LoginPage /> },
      { path: '/auth/callback',       element: <AuthCallbackPage /> },
      { path: '/auth/reset-password', element: <ResetPasswordPage /> },
      { path: '/preview/:token',      element: <RecipientPortalPage /> },
      { path: '/wrapped/s/:token',    element: <WrappedSharePage /> },

      // ── Semi-protected ─────────────────────────────────────────────────
      { path: '/auth/consent', element: <OAuthConsentPage /> },

      // ── Protected ──────────────────────────────────────────────────────
      { path: '/dashboard',  element: <ProtectedRoute><DashboardPage /></ProtectedRoute> },
      { path: '/onboarding', element: <ProtectedRoute><OnboardingPage /></ProtectedRoute> },
      { path: '/wrapped',    element: <ProtectedRoute><WrappedPage /></ProtectedRoute> },
      { path: '/calendar',   element: <ProtectedRoute><CalendarPage /></ProtectedRoute> },
      { path: '/account',    element: <ProtectedRoute><AccountPage /></ProtectedRoute> },
      { path: '/admin',      element: <ProtectedRoute><AdminPage /></ProtectedRoute> },

      // ── Catch-all ──────────────────────────────────────────────────────
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
